<script setup lang="ts">
import { computed, onMounted } from "vue";
import DocumentTable from "@/components/Documents/Table/DocumentTable.vue";
import { useSettingsStore } from "@/stores/settings";
import { useDocumentStore } from "@/stores/documents";

interface Props {
  shareId: string;
}

defineProps<Props>();

const settingsStore = useSettingsStore();
const documentStore = useDocumentStore();

const downloadProgress = computed(() => documentStore.downloadProgress);

const downloadDocument = async (itemId: string, itemName: string) => {
  try {
    const documentContentsAsBytes = await documentStore.downloadQuickShareDocument(itemId);
    const url = window.URL.createObjectURL(new Blob([documentContentsAsBytes]));
    const element = document.createElement("a");
    element.href = url;
    element.setAttribute("download", itemName);
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  } catch {
    /* empty */
  }
};

const onDriveItemClick = async (driveItem: any) => {
  await downloadDocument(driveItem.itemId, driveItem.name);
};

const downloadDocumentsAsZip = async () => {
  try {
    const zipContentsAsBytes = await documentStore.downloadQuickShareDocumentsAsZip();
    const url = window.URL.createObjectURL(new Blob([zipContentsAsBytes]));
    const element = document.createElement("a");
    element.href = url;
    element.setAttribute("download", `${settingsStore.quickShareTitle}.zip`);
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  } catch {
    /* empty */
  }
};

onMounted(async () => {
  try {
    await documentStore.loadQuickShareDocuments();
    // eslint-disable-next-line no-empty
  } catch {}
});
</script>

<template>
  <div class="home">
    <div class="mb-10">
      <h1>{{ settingsStore.quickShareTitle }}</h1>
    </div>
    <div>
      <ul>
        <li v-for="progressKey in Object.keys(downloadProgress)" :key="progressKey">
          {{ progressKey }}:
          {{
            downloadProgress[progressKey]
              ? downloadProgress[progressKey] === -1
                ? "Forbereder download..."
                : downloadProgress[progressKey] + "%"
              : "Færdig"
          }}
        </li>
      </ul>
    </div>

    <DocumentTable
      :table-caption="$t('Files.Files')"
      :drive-items="documentStore.quickShareDocuments"
      :show-icon="true"
      :show-name="true"
      :show-type="true"
      :show-size="true"
      :show-created-date="true"
      :show-shared-with="false"
      :show-modified-date="true"
      :show-modified-by="true"
      :is-clicable="true"
      :is-selectable="false"
      @driveItemClick="onDriveItemClick"
    />

    <div class="mt-4 text-right">
      <button
        class="button font-semibold text-primary-core opacity-70 mt-4 p-0 hover:underline"
        @click="downloadDocumentsAsZip"
      >
        <font-awesome-icon :icon="['far', 'download']" class="mr-3" />
        {{ $t("Files.DownloadAllAsZip") }}
      </button>
    </div>
  </div>
</template>
